import React from 'react';
import { Home, Plane, MapPin, ArrowRight } from 'lucide-react';
import { UseCase } from '../types/weather';

interface UseCaseSelectorProps {
  onUseCaseSelect: (useCase: UseCase) => void;
}

const getDefaultUseCases = () => [
  {
    id: 'planning-move' as UseCase,
    title: 'Planning a Move',
    description: 'Find the perfect place to live based on year-round weather conditions',
    icon: Home,
    color: 'from-blue-500 to-blue-600',
    features: [
      'Compare multiple locations',
      'Year-round climate analysis',
      'Perfect weather score for each location',
      'See how many days match your ideal conditions'
    ]
  },
  {
    id: 'planning-trip' as UseCase,
    title: 'Planning a Trip',
    description: 'Discover the best destinations or times to travel based on weather',
    icon: Plane,
    color: 'from-green-500 to-green-600',
    features: [
      'Find best destinations for specific dates',
      'Discover ideal travel times for locations',
      'Avoid rain/snow or find perfect temperatures',
      'Weather-based travel recommendations'
    ]
  },
  {
    id: 'my-location' as UseCase,
    title: 'My Location',
    description: 'See what the weather is typically like where you are right now',
    icon: MapPin,
    color: 'from-purple-500 to-purple-600',
    features: [
      'Current location weather patterns',
      'Historical data for today ±7 days',
      'What to expect this time of year',
      'Based on 20+ years of weather data'
    ]
  }
];

export const UseCaseSelector: React.FC<UseCaseSelectorProps> = ({ onUseCaseSelect }) => {
  const useCases = getDefaultUseCases();
  const headline = 'What would you like to know about the weather?';
  const subtitle = 'Choose your goal below and we\'ll help you make weather-informed decisions with 20+ years of historical data.';
  const footerText = 'Powered by 20+ years of NOAA weather data';

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {headline}
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          {subtitle}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {useCases.map((useCase) => {
          const Icon = useCase.icon;
          return (
            <div
              key={useCase.id}
              onClick={() => onUseCaseSelect(useCase.id)}
              className="group cursor-pointer transform transition-all duration-200 hover:scale-105"
            >
              <div className="card h-full border-2 border-transparent group-hover:border-gray-300 dark:group-hover:border-gray-600 transition-colors duration-200">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${useCase.color} flex items-center justify-center mb-6 group-hover:shadow-lg transition-shadow duration-200`}>
                  <Icon className="h-8 w-8 text-white" />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                  {useCase.title}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  {useCase.description}
                </p>
                
                <ul className="space-y-2 mb-6 flex-grow">
                  {useCase.features.map((feature: string, index: number) => (
                    <li key={index} className="flex items-start text-sm text-gray-500 dark:text-gray-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 mt-2 mr-3 flex-shrink-0"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <div className="flex items-center text-blue-600 dark:text-blue-400 font-medium group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-200">
                  <span>Get started</span>
                  <ArrowRight className="h-4 w-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-16 text-center">
        <div className="inline-flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded-full">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>{footerText}</span>
        </div>
      </div>
    </div>
  );
};
